import type { DraftFunction } from 'use-immer'

import { readAppJsonToModel, writeModelToAppJson } from '@/features/controller/configuration/models/appConfig'
import type { AppConfig } from '@/features/controller/configuration/models/appConfigSchema'
import { extractErrors } from '@/features/controller/configuration/models/raspiProperties/errors'
import {
  readRaspiPropertiesToModel,
  writeRaspiPropertiesToPropertiesText,
} from '@/features/controller/configuration/models/raspiProperties/main'
import type { RaspiPropertiesConfig } from '@/features/controller/configuration/models/raspiProperties/types'
import type { ControllerConfigVersion } from '@/features/controller/configuration/types'
import { hasKnownBasicPowerMeters } from '@/features/controller/configuration/utils/serialPortUsage'

export interface MultiModalConfiguration {
  source: ControllerConfigurationSourceText
  model: ControllerConfigurationModel
}

export interface ControllerConfigurationModel {
  appConfig: AppConfig | null
  raspiPropertiesConfig: RaspiPropertiesConfig | null
  errors: string[]
}

export interface ControllerConfigurationSourceText {
  appJsonText?: string
  raspiPropertiesText?: string
}

export interface ControllerConfigurationModelUpdate {
  updateRaspiPropertiesConfig?: DraftFunction<RaspiPropertiesConfig>
  updateAppConfig?: DraftFunction<AppConfig>
  comment?: string
}

export interface ControllerConfigurationSourceUpdate {
  appJsonText?: string
  comment?: string
  raspiPropertiesText?: string
}

export interface UnsavedConfiguration {
  value: string
}

export interface LatestConfigurationVersions {
  appJson: ControllerConfigVersion | null
  raspiProperties: ControllerConfigVersion | null
}

export function readControllerConfigurationModelFromSource({
  appJsonText,
  raspiPropertiesText,
}: {
  appJsonText?: string
  raspiPropertiesText?: string
}): ControllerConfigurationModel {
  const errors: string[] = []
  return {
    appConfig: appJsonText !== undefined ? readAppJsonToModel(appJsonText) : null,
    raspiPropertiesConfig:
      raspiPropertiesText !== undefined ? extractErrors(readRaspiPropertiesToModel(raspiPropertiesText), errors) : null,
    errors: errors,
  }
}

export function writeControllerConfigurationModelToSource(
  model: ControllerConfigurationModel,
  previousRaspiPropertiesText?: string,
): ControllerConfigurationSourceText {
  const updatedRaspiProperties = model.raspiPropertiesConfig
    ? writeRaspiPropertiesToPropertiesText(
        previousRaspiPropertiesText!,
        model.raspiPropertiesConfig,
        hasKnownBasicPowerMeters(model.appConfig, model.raspiPropertiesConfig),
      )
    : undefined
  const updatedAppJson = model.appConfig ? writeModelToAppJson(model.appConfig) : undefined

  return {
    appJsonText: updatedAppJson,
    raspiPropertiesText: updatedRaspiProperties,
  }
}
